import { memo, useCallback, useMemo } from 'react';
import { RackAlongsideExtension, RackingArea } from '@thive/fddm-types';

import { GapHorizontalIcon } from '@/assets/icons';
import { SelectInput } from '@/modules/common/components/inputs';
import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { NumberInput } from '@/modules/common/components/inputs/components/NumberInput';
import { NumUtils } from '@/modules/common/utils';
import { getCommonNumber, getCommonOption } from '@/modules/common/helpers/commonValue';
import { DisabledReasonTooltip } from '../common/DisabledReasonTooltip';
import {
  createEmptyAreas,
  pushAreaToItsCollection,
} from '@/modules/floorplanDesign/helpers/helpers';
import { useTranslatedOptions } from '@/modules/common/hooks';

const POI_DIRECTION_OPTIONS: {
  labelKey: string;
  value: RackAlongsideExtension['poiDirection'];
}[] = [
  {
    labelKey: 'interface:properties.area.rack.poi_direction_options.left_to_right',
    value: 'LEFT_TO_RIGHT',
  },
  {
    labelKey: 'interface:properties.area.rack.poi_direction_options.right_to_left',
    value: 'RIGHT_TO_LEFT',
  },
  {
    labelKey: 'interface:properties.area.rack.poi_direction_options.both_directions',
    value: 'BOTH_DIRECTIONS',
  },
];

interface Props {
  selectedRackingAreas: RackingArea[];
}

function AlongsideExtensionComponent({ selectedRackingAreas }: Props) {
  const poiDirectionOptions = useTranslatedOptions(POI_DIRECTION_OPTIONS);
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);

  // Shown when any selected rack is alongside; only editable when all of them are
  const alongsideExtensions = useMemo(
    () =>
      selectedRackingAreas.flatMap(({ alongsideExtension }) =>
        alongsideExtension ? [alongsideExtension] : [],
      ),
    [selectedRackingAreas],
  );

  const aisleWidthMm = useMemo(
    () =>
      getCommonNumber(
        alongsideExtensions.map(({ aisleWidth }) => NumUtils.metersToMmRounded(aisleWidth)),
      ),
    [alongsideExtensions],
  );

  const poiDirection = useMemo(
    () => getCommonOption(alongsideExtensions.map((extension) => extension.poiDirection)),
    [alongsideExtensions],
  );

  const onAisleWidthChange = useCallback(
    (newValueMm: number) => {
      if (!newValueMm || Number.isNaN(newValueMm)) return;

      const newValue = NumUtils.mmToMeters(newValueMm);
      fddmSvc.setPreference('rackingArea.alongsideExtension.aisleWidth', newValue);

      const updatedAreas = createEmptyAreas();
      selectedRackingAreas.forEach((area) => {
        pushAreaToItsCollection(updatedAreas, {
          ...area,
          alongsideExtension: {
            aisleWidth: newValue,
            poiDirection: area.alongsideExtension?.poiDirection ?? 'BOTH_DIRECTIONS',
          },
        });
      });
      fddmSvc.updateAreas(updatedAreas);
    },
    [fddmSvc, selectedRackingAreas],
  );

  const onPoiDirectionChange = useCallback(
    (newValue: RackAlongsideExtension['poiDirection']) => {
      fddmSvc.setPreference('rackingArea.alongsideExtension.poiDirection', newValue);

      const updatedAreas = createEmptyAreas();
      selectedRackingAreas.forEach((area) => {
        pushAreaToItsCollection(updatedAreas, {
          ...area,
          alongsideExtension: {
            aisleWidth: area.alongsideExtension?.aisleWidth ?? 0,
            poiDirection: newValue,
          },
        });
      });
      fddmSvc.updateAreas(updatedAreas);
    },
    [fddmSvc, selectedRackingAreas],
  );

  if (alongsideExtensions.length === 0) return null;

  const isPartialSelection = alongsideExtensions.length !== selectedRackingAreas.length;
  const disabledReasonKey = isPartialSelection
    ? 'interface:properties.area.rack.partial_selection.only_alongside'
    : undefined;

  return (
    <>
      <PropertiesLabel i18nkey='interface:properties.area.aisle_width'>
        <DisabledReasonTooltip reasonKey={disabledReasonKey}>
          <NumberInput
            // undefined (not false) keeps NumberInput's own read-only-mode check
            disabled={isPartialSelection ? true : undefined}
            value={aisleWidthMm}
            unit='mm'
            prefix={<GapHorizontalIcon />}
            sx={{ width: '100%' }}
            minValue={0}
            onChange={onAisleWidthChange}
          />
        </DisabledReasonTooltip>
      </PropertiesLabel>
      <PropertiesLabel i18nkey='interface:properties.area.poi_direction'>
        <DisabledReasonTooltip reasonKey={disabledReasonKey}>
          <SelectInput
            disabled={isPartialSelection}
            options={poiDirectionOptions}
            onChange={onPoiDirectionChange}
            value={poiDirection}
            showMixed
          />
        </DisabledReasonTooltip>
      </PropertiesLabel>
    </>
  );
}

export const AlongsideExtension = memo(AlongsideExtensionComponent);
